import { useCallback, useEffect, useState } from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel } from "@/components/app/PageParts";
import { AffiliateApply } from "@/components/app/AffiliateApply";
import { api, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";

export const usd = (c) => `$${(c / 100).toFixed(2)}`;

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg border bg-white p-4"><p className="text-xs text-slate-500">{label}</p><p className="font-display text-xl font-bold text-navy" data-testid={testId}>{value}</p></div>
);

export default function Affiliate() {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/affiliates/me").then(({ data }) => setD(data)).catch((e) => toast.error(errorMessage(e))); }, []);
  useEffect(load, [load]);
  if (!d) return null;
  const a = d.affiliate, s = d.stats;
  const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied"));
  return (
    <div data-testid="affiliate-page">
      <PageHeader eyebrow="Partners" title="Affiliate program" subtitle={`Earn ${d.terms.rate_percent}% of what EVR collects from customers you refer, for their first ${d.terms.months} months.`} />
      {d.mode === "test" && <p className="mb-6 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="affiliate-test-mode">Billing is in test mode, so commissions shown here are test amounts, not real money.</p>}
      {!a && <Panel title="Apply to join" testId="affiliate-apply-panel"><AffiliateApply terms={d.terms} onApplied={load} /></Panel>}
      {a && a.status !== "approved" && <p data-testid={`affiliate-status-${a.status}`} className="rounded-md border bg-white p-5 text-sm text-slate-700">{a.status === "pending" ? "Your application is waiting for the owner's review." : `Your affiliate account is ${a.status}.`}</p>}
      {a && a.status === "approved" && (
        <div className="space-y-6">
          <Panel title="Your referral link" testId="affiliate-link-panel">
            <div className="space-y-3 p-5">
              <div className="flex flex-wrap items-center gap-2"><code className="break-all rounded bg-slate-50 px-2 py-1 text-sm text-navy" data-testid="affiliate-link">{a.link}</code>
                <Button size="sm" variant="outline" onClick={() => copy(a.link)} data-testid="affiliate-copy-link"><Copy className="mr-1 h-4 w-4" /> Copy</Button></div>
              <p className="text-xs text-slate-500">Required disclosure, shown close to your recommendation:</p>
              <p className="rounded border-l-2 border-gold bg-gold-light/40 px-3 py-2 text-sm text-slate-700" data-testid="affiliate-disclosure">"{d.disclosure}"</p>
              <p className="text-xs text-slate-500">Describe EVR only as a tool that helps people understand paperwork, organize actions and track dates they confirm. Don't promise legal, tax or compliance outcomes.</p>
            </div>
          </Panel>
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Link clicks" value={s.clicks} testId="affiliate-stat-clicks" />
            <Stat label="Sign-ups" value={s.signups} testId="affiliate-stat-signups" />
            <Stat label="Paying customers" value={s.paying_customers} testId="affiliate-stat-paying" />
            <Stat label="On 30-day hold" value={usd(s.held_cents)} testId="affiliate-stat-held" />
            <Stat label="Available" value={usd(s.available_cents)} testId="affiliate-stat-available" />
            <Stat label="Paid to you" value={usd(s.paid_cents)} testId="affiliate-stat-paid" />
          </div>
          {s.refund_adjustments_cents < 0 && <p className="text-xs text-slate-500" data-testid="affiliate-refund-note">Includes {usd(-s.refund_adjustments_cents)} removed for refunds.</p>}
          <Panel title="Payouts" testId="affiliate-payouts">
            {d.payouts.length ? <ul className="divide-y">{d.payouts.map((p) => <li key={p.id} className="flex justify-between px-5 py-3 text-sm"><span>{formatDate(p.paid_at)}</span><span className="font-medium text-navy">{usd(p.amount_cents)}</span></li>)}</ul>
              : <p className="px-5 py-4 text-sm text-slate-500">No payouts yet. Payouts are monthly once your available balance reaches {usd(d.terms.min_payout_cents)}.</p>}
          </Panel>
        </div>
      )}
    </div>
  );
}
